import React from "react";
import { Box, Option } from "@mui/joy";
import DarkTypography from "./DarkTypography";
import DarkSelect from "./DarkSelect";
import ExcursionYearSelector from "./ExcursionYearSelector";
import { MONTH_NAMES_FR } from "../../utils/dateTime";

/** Month value for "whole year" when `includeFullYearOption` is true. */
export const YEAR_MONTH_ALL = 0;

type YearMonthPeriodSelectorProps = {
  years: number[];
  selectedYear: number | undefined;
  onYearChange: (year: number) => void;
  selectedMonth: number;
  onMonthChange: (month: number) => void;
  includeFullYearOption?: boolean;
  monthLabel?: string;
  monthAndFiltersInline?: boolean;
  afterMonthSlot?: React.ReactNode;
  endSlot?: React.ReactNode;
};

export default function YearMonthPeriodSelector({
  years,
  selectedYear,
  onYearChange,
  selectedMonth,
  onMonthChange,
  includeFullYearOption = false,
  monthLabel = "Mois",
  monthAndFiltersInline = false,
  afterMonthSlot,
  endSlot,
}: YearMonthPeriodSelectorProps) {
  const handleMonthSelectChange = React.useCallback(
    (_: unknown, value: number | null) => {
      const v = value ?? (includeFullYearOption ? YEAR_MONTH_ALL : 1);
      onMonthChange(v);
    },
    [includeFullYearOption, onMonthChange],
  );

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <ExcursionYearSelector
        years={years}
        selectedYear={selectedYear}
        onYearChange={onYearChange}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: monthAndFiltersInline ? "row" : { xs: "column", sm: "row" },
          flexWrap: monthAndFiltersInline ? "nowrap" : "wrap",
          gap: monthAndFiltersInline ? { xs: 0.75, sm: 2 } : 2,
          mb: 2,
          width: "100%",
          maxWidth: "100%",
          alignItems: monthAndFiltersInline ? "flex-end" : { sm: "flex-end" },
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            flex: monthAndFiltersInline ? { xs: "1 1 0%", sm: "1 1 200px" } : { sm: "1 1 200px" },
            maxWidth: monthAndFiltersInline ? { xs: "none", sm: 280 } : { xs: "95vw", sm: 280 },
            minWidth: 0,
          }}
        >
          <DarkTypography
            level="body-sm"
            colorVariant="primary"
            sx={{ mb: monthAndFiltersInline ? { xs: 0.25, sm: 0.5 } : 0.5 }}
          >
            {monthLabel}
          </DarkTypography>
          <DarkSelect
            size="sm"
            value={selectedMonth}
            onChange={handleMonthSelectChange}
            aria-label={monthLabel}
            sx={{ width: "100%" }}
          >
            {includeFullYearOption && <Option value={YEAR_MONTH_ALL}>Toute l&apos;année</Option>}
            {MONTH_NAMES_FR.map((label, i) => (
              <Option key={i + 1} value={i + 1}>
                {label}
              </Option>
            ))}
          </DarkSelect>
        </Box>
        {afterMonthSlot}
        {endSlot != null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              alignSelf: monthAndFiltersInline ? "flex-end" : { xs: "stretch", sm: "center" },
              flexShrink: 0,
              minHeight: 36,
            }}
          >
            {endSlot}
          </Box>
        )}
      </Box>
    </Box>
  );
}
